/**
 * Groupings for field values. A type or field that groups declares `allGroups`, the
 * buckets a grouped collection shows, and `matchesGroup`, whether an item's value falls
 * in one; a type or field with both is groupable, and one missing either is not. Spread
 * the grouping that fits: `{ ...group.byTarget }`.
 */

import { compareText } from "./sort.js"
import type { BaseFieldType } from "./types.js"

/** A fixed list of buckets, or a function of the values present in the items and the
 *  field being grouped (a select returns the field's `values`). */
export type AllGroups =
  | readonly unknown[]
  | ((values: unknown[], field: { values?: unknown }) => unknown[])

/** Whether an item's value falls in a bucket. */
export type MatchesGroup = (value: unknown, group: unknown) => boolean

type Grouping = Required<Pick<BaseFieldType, "allGroups" | "matchesGroup">>

/** One bucket per distinct value, in text order. */
export const byValue: Grouping = {
  allGroups: (values) => [...new Set(values.filter((value) => value != null))].sort(compareText),
  matchesGroup: (value, group) => value === group,
}

/** True and false; an unset value counts as false. */
export const byBoolean: Grouping = {
  allGroups: [true, false],
  matchesGroup: (value, group) => (value ?? false) === group,
}

/** A select's options in the order the field lists them, so an unused option still gets
 *  a bucket; the distinct values when it lists none. */
export const byOption: Grouping = {
  allGroups: (values, field) =>
    Array.isArray(field.values) ? [...field.values] : [...new Set(values)],
  matchesGroup: (value, group) => value === group,
}

type Target = { id: unknown; [k: string]: unknown }

const asTargets = (value: unknown): Target[] =>
  (Array.isArray(value) ? value : value == null ? [] : [value]).filter(
    (t): t is Target => t != null && typeof t === "object" && "id" in t
  )

/** One bucket per distinct target entity, deduped by id. An item is in the bucket of
 *  every target it holds (a record linked to two orgs appears under both). */
export const byTarget: Grouping = {
  allGroups: (values) => {
    const byId = new Map<unknown, Target>()
    for (const value of values)
      for (const t of asTargets(value)) if (!byId.has(t.id)) byId.set(t.id, t)
    return [...byId.values()]
  },
  matchesGroup: (value, group) => asTargets(value).some((t) => t.id === (group as Target)?.id),
}
